// components/hauler/StateConfirmSheet.js
// Bottom-sheet confirm dialog for Hauler card-as-action lifecycle taps.
//
// Renders state-specific copy and a Cancel / Confirm button pair so the
// Hauler's intent is explicit before any state mutation fires. IC-safe:
// the sheet is voluntary, framed as a question, and Cancel is the
// default safe exit. Tapping the dim overlay also cancels.
//
// State → copy mapping is the only thing this component knows; the
// actual transition (markEnRoute / markArrived / open completion) is
// the caller's responsibility via onConfirm.

import React from "react";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
};

// Copy by current status (the OLD status, before the transition).
const COPY = {
  claimed: {
    title: "Heading out now?",
    body: "Customer will be notified.",
    confirmLabel: "Confirm — on the way",
  },
  en_route: {
    title: "Arrived at the location?",
    body: "",
    confirmLabel: "Confirm — arrived",
  },
  in_progress: {
    title: "Ready to verify and complete?",
    body: "Take verification photo and complete.",
    confirmLabel: "Continue to verification",
  },
};

export default function StateConfirmSheet({ status, busy, onCancel, onConfirm }) {
  const c = COPY[status] || COPY.claimed;

  return (
    <div
      onClick={onCancel}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 250,
        background: "rgba(0,0,0,.55)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: "100%",
          maxWidth: 520,
          background: COLORS.card,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          borderTop: `1px solid ${COLORS.border}`,
          borderLeft: `1px solid ${COLORS.border}`,
          borderRight: `1px solid ${COLORS.border}`,
          padding: "20px 20px calc(28px + env(safe-area-inset-bottom, 0px))",
          color: COLORS.text,
          boxShadow: "0 -8px 24px rgba(0,0,0,.35)",
        }}
      >
        {/* Drag handle visual cue */}
        <div style={{
          width: 36,
          height: 4,
          borderRadius: 999,
          background: "rgba(242,242,242,.25)",
          margin: "0 auto 14px",
        }} />

        <div style={{
          fontSize: 18,
          fontWeight: 700,
          color: COLORS.text,
          marginBottom: c.body ? 6 : 18,
        }}>
          {c.title}
        </div>

        {c.body && (
          <div style={{
            fontSize: 13,
            color: COLORS.sub,
            marginBottom: 18,
            lineHeight: 1.45,
          }}>
            {c.body}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={ghostBtn(busy)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={primaryBtn(busy)}
          >
            {busy ? "Working…" : c.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ghostBtn(busy) {
  return {
    flex: "0 0 auto",
    minWidth: 96,
    padding: "13px 18px",
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    background: "transparent",
    color: COLORS.text,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 700,
    cursor: busy ? "not-allowed" : "pointer",
    minHeight: 48,
  };
}

function primaryBtn(busy) {
  return {
    flex: 1,
    padding: "13px 18px",
    borderRadius: 12,
    border: "none",
    background: busy ? "rgba(3,166,106,.5)" : COLORS.green,
    color: "#FFFFFF",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 800,
    cursor: busy ? "wait" : "pointer",
    minHeight: 48,
  };
}
